import { motion } from "framer-motion";
import { Package, SlidersHorizontal, Truck } from "lucide-react";

const steps = [
  {
    number: "01",
    icon: Package,
    title: "Choose your base",
    description: "Pick from our curated collection of Andhra pickles and wellness recipes.",
    examples: ["Andhra Avakaya", "Guntur Chilli Pickle", "Lemon Ginger Pickle"],
  },
  {
    number: "02",
    icon: SlidersHorizontal,
    title: "Customize everything",
    description: "Adjust spice, salt, oil, and ingredients. Apply a smart profile or fine-tune it yourself.",
    examples: ["Spice Level", "Salt Control", "Ingredient Toggles"],
  },
  {
    number: "03",
    icon: Truck,
    title: "Order your version",
    description: "Your personalized recipe is prepared fresh and delivered to your doorstep.",
    examples: ["Fresh Preparation", "Quality Sealed", "Doorstep Delivery"],
  },
];

export const HowItWorks = () => {
  return (
    <section id="how-it-works" data-testid="how-it-works-section" className="py-24 lg:py-36 relative">
      <div className="section-container">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
          className="text-center max-w-2xl mx-auto mb-20"
        >
          <span className="text-sm font-medium text-earth-green uppercase tracking-wider">How It Works</span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-700 text-sw-text mt-4 leading-tight">
            Three steps to your{" "}
            <span className="font-accent italic text-earth-green">perfect blend</span>
          </h2>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-8 lg:gap-12">
          {steps.map((step, index) => (
            <motion.div
              key={step.number}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.7, delay: index * 0.2 }}
              data-testid={`step-${step.number}`}
              className="relative group"
            >
              <div className="bg-white rounded-3xl p-8 lg:p-10 border border-gray-100 shadow-sm hover:shadow-md transition-all duration-500 h-full">
                <div className="flex items-center gap-4 mb-6">
                  <span className="text-5xl font-heading font-800 text-cream-tertiary group-hover:text-earth-green/15 transition-colors duration-500">
                    {step.number}
                  </span>
                  <div className="w-12 h-12 rounded-2xl bg-earth-green/10 flex items-center justify-center">
                    <step.icon size={22} className="text-earth-green" />
                  </div>
                </div>

                <h3 className="text-xl font-heading font-700 text-sw-text mb-3">
                  {step.title}
                </h3>

                <p className="text-sm text-sw-text-secondary leading-relaxed mb-6">
                  {step.description}
                </p>

                <div className="flex flex-wrap gap-2">
                  {step.examples.map((ex) => (
                    <span
                      key={ex}
                      className="text-xs bg-cream-secondary text-sw-text-secondary px-3 py-1.5 rounded-full"
                    >
                      {ex}
                    </span>
                  ))}
                </div>
              </div>

              {index < steps.length - 1 && (
                <div className="hidden lg:block absolute top-1/2 -right-6 w-12">
                  <svg viewBox="0 0 48 8" className="text-cream-tertiary">
                    <path d="M0 4 L40 4 M36 0 L44 4 L36 8" fill="none" stroke="currentColor" strokeWidth="1.5" />
                  </svg>
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
